<template>
  <v-dialog v-model="dialog" max-width="700" persistent>
    <v-card class="rounded-xl">
      <!-- Header with gradient background -->
      <v-card-title class="white--text gradient-header py-4">
        <div class="d-flex align-center">
          <v-icon large color="white" class="mr-3">mdi-information-outline</v-icon>
          <span class="text-h5 font-weight-medium">Response Details</span>
        </div>
        <v-spacer />
        <v-btn icon dark @click="onClose">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-card-title>

      <v-card-text class="pa-6">
        <template v-if="item">
          <!-- Main content with modern cards -->
          <v-row class="mb-4">
            <v-col cols="12" md="6">
              <v-card flat class="rounded-lg pa-4 h-100" color="#f8f9fa">
                <div class="d-flex align-center mb-3">
                  <v-icon color="primary" class="mr-2">mdi-account-circle</v-icon>
                  <h3 class="text-h6 primary--text">Volunteer</h3>
                </div>
                <v-divider class="mb-4"></v-divider>
                <div class="d-flex align-start">
                  <v-avatar size="80" class="mr-4 elevation-2">
                    <v-img :src="item.volunteer?.thumbnail || require('@/assets/default.jpg')" />
                  </v-avatar>
                  <div>
                    <p class="text-h6 mb-1 font-weight-medium">{{ fullName(item.volunteer) }}</p>
                    <div class="d-flex align-center mb-1">
                      <v-icon small color="grey" class="mr-2">mdi-email</v-icon>
                      <span>{{ item.volunteer?.email || 'Not provided' }}</span>
                    </div>
                    <div class="d-flex align-center">
                      <v-icon small color="grey" class="mr-2">mdi-phone</v-icon>
                      <span>{{ item.volunteer?.phone || 'Not provided' }}</span>
                    </div>
                  </div>
                </div>
              </v-card>
            </v-col>

            <v-col cols="12" md="6">
              <v-card flat class="rounded-lg pa-4 h-100" color="#f8f9fa">
                <div class="d-flex align-center mb-3">
                  <v-icon color="primary" class="mr-2">mdi-calendar-clock</v-icon>
                  <h3 class="text-h6 primary--text">Shift</h3>
                </div>
                <v-divider class="mb-4"></v-divider>
                <p class="text-subtitle-1 font-weight-medium mb-2">{{ item.shift?.title || 'No title' }}</p>
                <p class="text-caption grey--text mb-3">{{ item.shift?.description || 'No description' }}</p>
                <div class="d-flex">
                  <v-chip small color="blue lighten-5" class="mr-2 blue--text">
                    <v-icon small left>mdi-clock-start</v-icon>
                    {{ formatDate(item.shift?.start_date) }}
                  </v-chip>
                  <!-- <v-chip small color="blue lighten-5" class="blue--text">
                    <v-icon small left>mdi-clock-end</v-icon>
                    {{ formatDate(item.shift?.end_date) }}
                  </v-chip> -->
                </div>
              </v-card>
            </v-col>
          </v-row>

          <!-- Response details section -->
          <v-card flat class="rounded-lg pa-4 mb-4" color="#f8f9fa">
            <div class="d-flex align-center mb-3">
              <v-icon color="primary" class="mr-2">mdi-comment-text-outline</v-icon>
              <h3 class="text-h6 primary--text">Response Details</h3>
            </div>
            <v-divider class="mb-4"></v-divider>
            <v-row>
              <v-col cols="12" md="4">
                <p class="mb-1 text-caption grey--text">Status</p>
                <v-chip :color="getStatusColor(item.status)" dark>
                  {{ item.status }}
                </v-chip>
              </v-col>
              <v-col cols="12" md="4">
                <p class="mb-1 text-caption grey--text">Created At</p>
                <p class="font-weight-medium">{{ formatDate(item.createdAt) }}</p>
              </v-col>
            </v-row>
            <v-row>
              <v-col cols="12">
                <p class="mb-1 text-caption grey--text">Notes</p>
                <v-textarea
                  :value="item.note || 'No notes provided'"
                  readonly
                  outlined
                  dense
                  auto-grow
                  class="rounded-lg"
                  background-color="white"
                ></v-textarea>
              </v-col>
            </v-row>
          </v-card>
        </template>

        <template v-else>
          <v-alert type="error" class="ma-4 rounded-lg">
            No response data available
          </v-alert>
        </template>
      </v-card-text>

      <v-card-actions class="px-6 pb-5">
        <v-spacer />
        <v-btn
          color="primary"
          large
          depressed
          class="px-6 rounded-lg"
          @click="onClose"
        >
          Close
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
export default {
  props: {
    dialog: {
      type: Boolean,
      required: true
    },
    onCloseDialog: {
      type: Function,
      default: () => {}
    },
    item: {
      type: Object,
      default: null
    }
  },
  methods: {
    onClose() {
      this.$emit('update:dialog', false)
      this.onCloseDialog()
    },
    formatDate(date) {
      if (!date) return 'Not specified'
      try {
        return new Date(date).toLocaleDateString('en-US', {
          year: 'numeric',
          month: 'short',
          day: 'numeric',
          hour: '2-digit',
          minute: '2-digit'
        })
      } catch (e) {
        console.error('Date formatting error:', e)
        return 'Invalid date'
      }
    },
    fullName(volunteer) {
      if (!volunteer) return 'Unknown Volunteer'
      return `${volunteer.first_name || ''} ${volunteer.last_name || ''}`.trim() || 'Unnamed Volunteer'
    },
    getStatusColor(status) {
      switch (status) {
        case "Rejected":
          return "error";
        case "Confirmed":
          return "success";
        case "Pending":
          return "warning";
        default:
          return "grey";
      }
    }
  }
}
</script>

<style scoped>
.gradient-header {
  background: linear-gradient(135deg,#084468,#0a5b8a);
}

.v-card {
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.05);
}

.v-chip {
  font-weight: 500;
}

.h-100 {
  height: 100%;
}

.rounded-xl {
  border-radius: 16px !important;
}

.rounded-lg {
  border-radius: 12px !important;
}

.v-textarea::v-deep textarea {
  color: #333 !important;
}
</style>